/**
 * System Section
 * Displays system metadata and audit information
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { DcsApp } from '../../types/general-settings-types';
import { Input } from '@/core/components/ui/input';
import { Label } from '@/core/components/ui/label';

interface SystemSectionProps {
  app: DcsApp;
  onFieldChange: (field: string, value: any) => void;
  hasChanges?: boolean;
  isOpen: boolean;
  onToggle: () => void;
}

export const SystemSection = ({ app, onFieldChange, hasChanges, isOpen, onToggle }: SystemSectionProps) => {
  const { t } = useTranslation('assignEv');

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      {/* Section Header */}
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between bg-gray-100 hover:bg-gray-200 transition-colors"
      >
        <div className="flex items-center gap-3">
          {isOpen ? (
            <ChevronDown className="w-5 h-5 text-gray-700" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-700" />
          )}
          <h3 className="font-bold text-gray-900 text-base">{t('general.systemSection.title')}</h3>
          {hasChanges && (
            <span className="ml-2 px-3 py-1 text-xs font-bold text-white bg-orange-500 rounded-full shadow-md">
              {t('badges.modified')}
            </span>
          )}
        </div>
      </button>

      {/* Section Content */}
      {isOpen && (
        <div className="px-6 py-4 bg-white space-y-6">
          {/* Creation Info Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-medium text-gray-600 mb-2 block">
                {t('general.systemSection.fields.createdDate')}
              </Label>
              <Input
                value={formatDate(app.created)}
                disabled
                className="bg-white border-gray-300 text-gray-600"
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-gray-600 mb-2 block">
                {t('general.systemSection.fields.createdBy')}
              </Label>
              <Input
                value={app.createdByName || app.createdBy || ''}
                disabled
                className="bg-white border-gray-300 text-gray-600"
              />
            </div>
          </div>

          {/* Update Info Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-medium text-gray-600 mb-2 block">
                {t('general.systemSection.fields.updatedDate')}
              </Label>
              <Input
                value={formatDate(app.updated)}
                disabled
                className="bg-white border-gray-300 text-gray-600"
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-gray-600 mb-2 block">
                {t('general.systemSection.fields.updatedBy')}
              </Label>
              <Input
                value={app.updatedByName || app.updatedBy || ''}
                disabled
                className="bg-white border-gray-300 text-gray-600"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
